/**
 * commentTemplates — plantillas por vertical para la feature
 * "comentario con palabra clave → DM" (COMMENT-RULES-SCHEMA.md §10).
 *
 * Se precargan al ASIGNAR un bot a una cuenta de canal, derivando el vertical de
 * `bot.config.industry`. Son un PUNTO DE PARTIDA editable, no configuración
 * final: el ICP (corredores, dueños de clínica) no es técnico y un formulario en
 * blanco con "keywords" es fricción.
 *
 * INDEXADAS POR `industry`, NO POR `templateId`. Tres de los siete valores
 * difieren entre uno y otro (abogado→legal, taller_mecanico→automotriz,
 * otros_servicios→otros), así que indexar por templateId dejaría a esos tres
 * verticales sin plantilla.
 *
 * REGLAS DEL COPY (§10.0), no negociables:
 *   - ESPAÑOL NEUTRO, sin voseo. Los defaults SE HEREDAN: si vienen con voseo,
 *     cada cliente que los acepte publica voseo bajo SU marca sin revisarlo.
 *   - Tildes correctas, sobre todo en el texto PÚBLICO, que ve cualquiera que
 *     pase por el post.
 *   - SIN placeholders de nombre de negocio. La convención [NOMBRE_CLINICA] de
 *     funnelTemplates se rellena en applyTemplate, no acá; si el relleno fallara,
 *     el cliente publicaría literalmente "[NOMBRE_CLINICA]" a un prospecto. El DM
 *     sale desde la cuenta, así que el remitente ya es evidente.
 *   - SIN precios. Dependen del cliente y quedarían obsoletos sin que nada falle.
 *
 * DOS REGLAS POR VERTICAL: una de ACCIÓN (priority 10) y una de PRECIO
 * (priority 20). La de acción gana cuando ambas matchean, porque pedir hora es
 * más intención que preguntar valor; si sólo hay intención de precio, la de
 * acción no matchea y cae la otra.
 */

import { normalizeCommentText } from './commentMatching';

// Familia de precio compartida por los siete verticales. Incluye FLEXIONES, no
// sólo sinónimos (§10.1): el límite de palabra de matchKeyword hace que "precio"
// NO matchee "precios" — el mismo mecanismo que impide que matchee "precioso" —
// así que singular y plural van ambos.
const PRECIO_BASE = [
  'precio', 'precios', 'valor', 'valores', 'cuanto',
  'cuanto sale', 'cuanto vale', 'costo', 'costos', 'tarifa', 'tarifas',
];

// El texto público no necesita ser específico del vertical: quien lo lee ya está
// en el post. Rotan para no repetir el mismo string en muchos comentarios, que es
// lo que dispara el antispam de Instagram.
export const PUBLIC_REPLY_DEFAULTS = {
  enabled: true,
  mode: 'fixed',
  templates: ['Te escribí por DM 📩', 'Te respondo por interno 💬'],
  fallbackTemplates: ['Escríbenos por DM y te contamos 💬'],
};

export const COMMENT_TEMPLATES = {
  salud_estetica: {
    label: 'Salud y Estética',
    rules: [
      {
        key: 'agenda',
        label: 'Piden hora',
        keywords: ['hora', 'horas', 'agendar', 'cita', 'citas', 'reservar', 'reserva', 'reservas', 'disponibilidad', 'disponible', 'turno', 'turnos'],
        dmText: '¡Hola! Vi tu comentario 👋 Con gusto te ayudo a reservar tu hora. ¿Qué tratamiento te interesa y qué días te acomodan? Así te confirmo la disponibilidad.',
      },
      {
        key: 'precio',
        label: 'Preguntan precio',
        keywords: [...PRECIO_BASE, 'arancel', 'aranceles'],
        dmText: '¡Hola! Vi tu comentario 👋 Te paso los valores por acá. ¿Qué tratamiento te interesa? Así te doy el detalle y las alternativas que tenemos.',
      },
    ],
  },

  inmobiliaria: {
    label: 'Bienes Raíces',
    rules: [
      {
        key: 'visita',
        label: 'Piden visita',
        keywords: ['visita', 'visitas', 'visitar', 'agendar', 'conocer', 'mostrar', 'disponibilidad', 'disponible'],
        dmText: '¡Hola! Vi tu comentario 👋 Con gusto coordinamos una visita. ¿Qué día y horario te acomodan? Cuéntame también si buscas arriendo o compra.',
      },
      {
        key: 'precio',
        label: 'Preguntan precio',
        keywords: [...PRECIO_BASE, 'uf', 'arriendo', 'arriendos'],
        dmText: '¡Hola! Vi tu comentario 👋 Te paso el detalle de la propiedad por acá. ¿Buscas arriendo o compra, y en qué rango estás pensando? Así te muestro las opciones que calzan.',
      },
    ],
  },

  ecommerce: {
    label: 'E-commerce',
    rules: [
      {
        key: 'stock',
        label: 'Preguntan stock o envío',
        // `color`/`colores` quedaron FUERA: en un post de producto son halago casi
        // siempre ("hermoso color", "qué lindo color"), y la consulta real trae el
        // producto pegado ("tienen en negro"), que `talla`/`stock`/`despacho` ya
        // cubren. `stock` SÍ se queda: casi no aparece en halagos y es la señal
        // más directa de esta regla. La asimetría es deliberada.
        keywords: ['stock', 'talla', 'tallas', 'envio', 'envios', 'despacho', 'despachos', 'disponible', 'disponibles'],
        dmText: '¡Hola! Vi tu comentario 👋 Reviso stock y te confirmo. ¿Qué modelo, talla o color buscas? Dime tu comuna y calculo el despacho.',
      },
      {
        key: 'precio',
        label: 'Preguntan precio',
        keywords: [...PRECIO_BASE, 'oferta', 'ofertas', 'descuento', 'descuentos'],
        dmText: '¡Hola! Vi tu comentario 👋 Te paso los valores por acá. ¿Qué producto te interesa? Si me dices tu comuna, te confirmo también el despacho.',
      },
    ],
  },

  restaurante: {
    label: 'Restaurante',
    rules: [
      {
        key: 'reserva',
        label: 'Piden reserva o delivery',
        // `mesa`/`mesas` quedaron FUERA: "que linda mesa" es un halago frecuente y
        // recibiría un DM comercial. `reserva`/`reservar` cubre la intención real.
        keywords: ['reserva', 'reservas', 'reservar', 'horario', 'horarios', 'delivery', 'pedido', 'pedidos'],
        dmText: '¡Hola! Vi tu comentario 👋 Con gusto tomamos tu reserva. ¿Para qué día, hora y cuántas personas? Te confirmo la mesa enseguida.',
      },
      {
        key: 'precio',
        label: 'Piden carta o precios',
        keywords: [...PRECIO_BASE, 'carta', 'menu', 'menus'],
        dmText: '¡Hola! Vi tu comentario 👋 Te paso la carta y los valores por acá. ¿Buscas para comer en el local, retiro o delivery?',
      },
    ],
  },

  legal: {
    label: 'Servicios Legales',
    rules: [
      {
        key: 'consulta',
        label: 'Piden consulta',
        keywords: ['consulta', 'consultas', 'asesoria', 'asesorias', 'cita', 'citas', 'agendar', 'hora', 'horas', 'caso'],
        dmText: '¡Hola! Vi tu comentario 👋 Con gusto agendamos una consulta. ¿Me cuentas brevemente de qué se trata tu caso? Así te derivo con el abogado indicado.',
      },
      {
        key: 'precio',
        label: 'Preguntan honorarios',
        keywords: [...PRECIO_BASE, 'honorarios'],
        dmText: '¡Hola! Vi tu comentario 👋 Los honorarios dependen del tipo de caso y su complejidad. ¿Me cuentas brevemente qué necesitas? Así te doy un valor referencial.',
      },
    ],
  },

  automotriz: {
    label: 'Taller Mecánico',
    rules: [
      {
        key: 'agenda',
        label: 'Piden hora',
        // `mantencion`/`revision` quedaron FUERA: "cuanto sale la mantencion" es
        // intención de PRECIO y esta regla se evalúa primero, así que desviaba a
        // quien preguntaba valor hacia un formulario de marca y modelo.
        keywords: ['hora', 'horas', 'agendar', 'cita', 'citas', 'disponibilidad', 'taller'],
        dmText: '¡Hola! Vi tu comentario 👋 Con gusto te agendamos. ¿Qué vehículo tienes (marca, modelo, año) y qué necesitas revisar? Así te doy una hora.',
      },
      {
        key: 'precio',
        label: 'Piden presupuesto',
        keywords: [...PRECIO_BASE, 'presupuesto', 'presupuestos', 'cotizacion', 'cotizaciones'],
        dmText: '¡Hola! Vi tu comentario 👋 Para darte un presupuesto necesito un par de datos: ¿qué vehículo tienes y qué falla o servicio necesitas?',
      },
    ],
  },

  otros: {
    label: 'Otros Servicios',
    rules: [
      {
        key: 'contacto',
        label: 'Piden información',
        // `info` suelto quedó FUERA: aparece en demasiados halagos ("buena info",
        // "gracias por la info") y el límite de palabra no lo salva porque es una
        // palabra completa. `informacion` SÍ se queda: es la señal principal de
        // este cajón de sastre.
        keywords: ['informacion', 'agendar', 'cita', 'citas', 'hora', 'horas', 'disponibilidad', 'contacto'],
        dmText: '¡Hola! Vi tu comentario 👋 Con gusto te ayudo por acá. ¿Me cuentas qué necesitas? Así te doy el detalle y coordinamos.',
      },
      {
        key: 'precio',
        label: 'Preguntan precio',
        keywords: [...PRECIO_BASE, 'presupuesto', 'presupuestos', 'cotizacion', 'cotizaciones'],
        dmText: '¡Hola! Vi tu comentario 👋 Los valores dependen de lo que necesites. ¿Me cuentas un poco más? Así te paso el detalle y las alternativas.',
      },
    ],
  },
};

/** Verticales disponibles, para el selector de la UI cuando hay que preguntar. */
export function getAvailableVerticals() {
  return Object.entries(COMMENT_TEMPLATES).map(([id, v]) => ({ id, label: v.label }));
}

/**
 * Construye los docs de commentRules listos para escribir.
 *
 * Las keywords se normalizan ACÁ con la misma definición que usa el runtime al
 * matchear (módulo compartido con gate de paridad byte a byte). Normalizarlas al
 * construir y no al guardarlas a mano evita el fallo silencioso de §6: una
 * keyword escrita "Información" nunca matchearía "informacion" y no habría error
 * en ningún log.
 *
 * `seededFromIndustry` queda en cada regla para poder detectar después que el
 * cliente cambió el bot a otro vertical — se compara contra bot.config.industry
 * al renderizar y se AVISA, nunca se sobrescribe: las reglas editadas son trabajo
 * del cliente (misma lección que obligó al modal de confirmación de applyTemplate).
 *
 * @param {string} industry  uno de los 7 valores de bot.config.industry
 * @param {{accountId: string, canal?: string}} opts
 * @returns {Array<object>|null} null si el vertical no existe
 */
export function buildRulesForIndustry(industry, { accountId, canal = 'instagram' } = {}) {
  const vertical = COMMENT_TEMPLATES[industry];
  if (!vertical || !accountId) return null;

  return vertical.rules.map((rule, i) => ({
    accountId,
    canal,
    active: true,
    priority: (i + 1) * 10,          // acción 10, precio 20 — la de acción gana
    keywords: rule.keywords.map(normalizeCommentText).filter(Boolean),
    matchMode: 'contains',
    mediaScope: { type: 'all', mediaIds: [] },
    dm: { text: rule.dmText },
    publicReply: { ...PUBLIC_REPLY_DEFAULTS },
    handoffToBot: true,
    botId: null,                      // null = usa el botId de la cuenta
    cooldownHours: 24,
    stats: { matched: 0, sent: 0, failed: 0, lastMatchAt: null },
    seededFromIndustry: industry,
    seededRuleKey: rule.key,
  }));
}
